import { useState, useEffect, useContext } from "react";
import { Table, Spin, Button, Input, DatePicker, Select, Row, Col, Space, Tag, Modal, Typography, Timeline, Tooltip, Badge } from "antd";
import { CheckOutlined, CloseOutlined, LoadingOutlined } from "@ant-design/icons";
import axios from "axios";
import { AuthContext } from "../context/AuthContext";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "../components/TeamLeaves.css";
import dayjs from "dayjs";
import * as XLSX from "xlsx";

const { Text, Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

export default function PipApprovals() {
    const { authData, backUrl } = useContext(AuthContext);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);
    // PipApprovals is always an approval-step-based view (HOD/HR see their own queue).
    // Always use is_employee_view=true so the backend applies step-level filtering.
    // Filters and tabs states
    const [empCodeFilter, setEmpCodeFilter] = useState("");
    const [empNameFilter, setEmpNameFilter] = useState("");
    const [organisationFilter, setOrganisationFilter] = useState("");
    const [departmentFilter, setDepartmentFilter] = useState("");
    const [designationFilter, setDesignationFilter] = useState("");
    const [dateRangeFilter, setDateRangeFilter] = useState([]);
    const [approvalStatusFilter, setApprovalStatusFilter] = useState("Pending");

    const [organisations, setOrganisations] = useState([]);
    const [departments, setDepartments] = useState([]);
    const [designations, setDesignations] = useState([]);

    // Status counts (Pending / Approved / Rejected)
    const [statusCounts, setStatusCounts] = useState({
        Pending: 0,
        Approved: 0,
        Rejected: 0,
    });
    const [countsLoading, setCountsLoading] = useState(false);

    // Decision modals state
    const [showAcceptModal, setShowAcceptModal] = useState({ status: false, record: null });
    const [showRejectModal, setShowRejectModal] = useState({ status: false, record: null });
    const [remarks, setRemarks] = useState("");
    const [processing, setProcessing] = useState(false);

    // Fetch filters metadata
    const fetchOrganisations = async () => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/organisations/`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setOrganisations(res.data.results || []);
        } catch (err) {
            console.error("Error fetching organisations:", err);
        }
    };

    const fetchDepartments = async (orgId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/departments/?organisation=${orgId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDepartments(res.data.results || []);
        } catch (err) {
            console.error("Error fetching departments:", err);
        }
    };

    const fetchDesignations = async (deptId) => {
        try {
            const res = await axios.get(`${backUrl}/api/emp/designations/?department=${deptId}`, {
                headers: { Authorization: `Bearer ${authData?.access}` },
            });
            setDesignations(res.data.results || []);
        } catch (err) {
            console.error("Error fetching designations:", err);
        }
    };

    useEffect(() => {
        fetchOrganisations();
    }, []);

    const handleOrganisationChange = (value) => {
        setOrganisationFilter(value);
        setDepartmentFilter("");
        setDesignationFilter("");
        setDepartments([]);
        setDesignations([]);
        if (value) {
            const orgObj = organisations.find(o => o.name === value);
            if (orgObj) fetchDepartments(orgObj.id);
        }
    };

    const handleDepartmentChange = (value) => {
        setDepartmentFilter(value);
        setDesignationFilter("");
        setDesignations([]);
        if (value) {
            const deptObj = departments.find(d => d.name === value);
            if (deptObj) fetchDesignations(deptObj.id);
        }
    };

    // Build query params shared across status-specific requests (everything except `status`)
    const buildCommonParams = () => {
        const params = new URLSearchParams();
        const isEmpView = localStorage.getItem("Hrselected") === "employee";
        params.append("is_employee_view", isEmpView ? "true" : "false");
        if (isEmpView) {
            params.append("view_type", "approvals");
        }
        if (empCodeFilter) {
            params.append("emp_code", empCodeFilter);
        }
        if (empNameFilter) {
            params.append("emp_name", empNameFilter);
        }
        if (organisationFilter) {
            const orgObj = organisations.find(o => o.name === organisationFilter);
            if (orgObj) params.append("organisation", orgObj.id);
        }
        if (departmentFilter) {
            const deptObj = departments.find(d => d.name === departmentFilter);
            if (deptObj) params.append("department", deptObj.id);
        }
        if (designationFilter) {
            const desObj = designations.find(d => d.name === designationFilter);
            if (desObj) params.append("designation", desObj.id);
        }
        if (dateRangeFilter && dateRangeFilter.length === 2) {
            const [start, end] = dateRangeFilter;
            if (start && end) {
                params.append("from_date", start.format("YYYY-MM-DD"));
                params.append("to_date", end.format("YYYY-MM-DD"));
            }
        }
        return params;
    };

    const fetchPipTasks = async () => {
        setLoading(true);
        try {
            const params = buildCommonParams();
            if (approvalStatusFilter) {
                params.append("status", approvalStatusFilter);
            }

            const response = await axios.get(
                `${backUrl}/api/piprequest/pip-requests/?${params.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`,
                    },
                }
            );

            setData(response.data.results || []);
        } catch (error) {
            console.error(error);
            toast.error("Failed to load PIP tasks");
        } finally {
            setLoading(false);
        }
    };

    // Fetch Pending/Approved/Rejected counts in parallel, respecting all other active filters
    const fetchStatusCounts = async () => {
        setCountsLoading(true);
        try {
            const statuses = ["Pending", "Approved", "Rejected"];

            const requests = statuses.map((status) => {
                const params = buildCommonParams();
                params.append("status", status);
                // Keep the payload light — we only need the total count
                params.append("page_size", "1");

                return axios.get(
                    `${backUrl}/api/piprequest/pip-requests/?${params.toString()}`,
                    {
                        headers: {
                            Authorization: `Bearer ${authData?.access}`,
                        },
                    }
                );
            });

            const [pendingRes, approvedRes, rejectedRes] = await Promise.all(requests);

            const getCount = (res) =>
                typeof res.data?.count === "number"
                    ? res.data.count
                    : (res.data?.results?.length || 0);

            setStatusCounts({
                Pending: getCount(pendingRes),
                Approved: getCount(approvedRes),
                Rejected: getCount(rejectedRes),
            });
        } catch (error) {
            console.error("Error fetching status counts:", error);
        } finally {
            setCountsLoading(false);
        }
    };

    useEffect(() => {
        fetchPipTasks();
        fetchStatusCounts();
    }, [
        approvalStatusFilter,
        empCodeFilter,
        empNameFilter,
        organisationFilter,
        departmentFilter,
        designationFilter,
        dateRangeFilter,
    ]);

    const handleDecision = async (record, action) => {
        if (action === "Rejected" && !remarks.trim()) {
            toast.error("Please enter a reason for rejection.");
            return;
        }
        setProcessing(true);
        try {
            const stepId = record.user_approval_step_id;
            await axios.post(
                `${backUrl}/api/piprequest/pip-approval-tasks/${stepId}/submit-decision/`,
                { action: action, remarks: remarks.trim() || "N/A" },
                {
                    headers: {
                        Authorization: `Bearer ${authData?.access}`
                    }
                }
            );
            toast.success(`PIP request ${action} successfully!`);
            setShowAcceptModal({ status: false, record: null });
            setShowRejectModal({ status: false, record: null });
            setRemarks("");
            fetchPipTasks();
            fetchStatusCounts();
        } catch (error) {
            console.error(error);
            toast.error(error?.response?.data?.error || error?.response?.data?.detail || "Failed to submit decision");
        } finally {
            setProcessing(false);
        }
    };

    // Format datetime
    const formatDate = (dateString) => {
        if (!dateString) return "—";
        const date = new Date(dateString);
        return date.toLocaleString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
        });
    };

    const downloadExcel = () => {
        const rows = data.map((item) => ({
            "Employee Code": item.employee?.emp_code || item.employee?.user?.username || "-",
            "Employee Name": item.employee_name || "-",
            "Created By": item.created_by_name || "-",
            "Created On": formatDate(item.created_at),
            "PIP Days": item.pip_days || "-",
            "Overall Status": item.overall_status || "-",
            "Reason": item.reason || "-",
        }));
        const worksheet = XLSX.utils.json_to_sheet(rows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "PIP Requests");
        XLSX.writeFile(workbook, `${approvalStatusFilter}_PIP_Requests.xlsx`);
    };

    const columns = [
        { title: "ID", dataIndex: "id", key: "id", width: 70 },
        {
            title: "Employee Code",
            key: "employee_code",
            render: (_, record) => record.employee?.emp_code || record.employee?.user?.username || "—"
        },
        { title: "Employee Name", dataIndex: "employee_name", key: "employee_name" },
        { title: "Created By", dataIndex: "created_by_name", key: "created_by_name" },
        {
            title: "Created On",
            dataIndex: "created_at",
            key: "created_at",
            render: (value) => formatDate(value),
        },
        { title: "PIP Days", dataIndex: "pip_days", key: "pip_days", width: 100 },
        {
            title: "Overall Status",
            dataIndex: "overall_status",
            key: "overall_status",
            render: (status) => {
                const color =
                    status === "Approved"
                        ? "green"
                        : status === "Rejected"
                            ? "red"
                            : "#faad14";
                return (
                    <span style={{ color, fontWeight: 600 }}>{status ?? "-"}</span>
                );
            },
        },
        {
            title: "Reason",
            dataIndex: "reason",
            key: "reason",
            width: 220,
            render: (text) =>
                text ? (
                    <Tooltip title={text} placement="topLeft">
                        <div
                            style={{
                                maxWidth: 200,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                cursor: "pointer",
                            }}
                        >
                            {text}
                        </div>
                    </Tooltip>
                ) : "—",
        },
        {
            title: "Approval Hierarchy",
            key: "remarks",
            width: 450,
            render: (_, record) => {
                const approvals = record.approvals || [];
                if (!approvals.length) return "—";

                return (
                    <div style={{ marginTop: "16px" }}>
                        <Timeline
                            items={approvals.map((a, index) => {
                                const isRejected = a.status === "Rejected";
                                const isApproved = a.status === "Approved";
                                const timelineColor = isRejected ? "red" : isApproved ? "green" : "gray";

                                return {
                                    color: timelineColor,
                                    children: (
                                        <div style={{ marginBottom: "8px" }}>
                                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "8px" }}>
                                                <Text strong style={{ fontSize: "15px" }}>
                                                    {a.approver_name}
                                                </Text>
                                                <Tag color={isRejected ? "error" : isApproved ? "success" : "default"} style={{ margin: 0 }}>
                                                    {a.status}
                                                </Tag>
                                            </div>
                                            {a.completed_on && (
                                                <div style={{ color: "#595959", fontSize: "12px", marginBottom: "2px" }}>
                                                    <Text strong style={{ fontSize: "11px", color: "#595959" }}>{isRejected ? "Rejected On: " : "Approved On: "}</Text>
                                                    {dayjs(a.completed_on).format("DD MMM YYYY [at] hh:mm A")}
                                                </div>
                                            )}
                                            {a.remarks && (
                                                <div style={{ fontSize: "13px" }}>
                                                    <Text strong>{isRejected ? "Rejection Remark: " : "Remark: "}</Text>
                                                    <Text>{a.remarks}</Text>
                                                </div>
                                            )}
                                        </div>
                                    ),
                                };
                            })}
                        />
                    </div>
                );
            }
        }
    ];

    if (approvalStatusFilter === "Pending") {
        columns.push(
            {
                title: "Approve",
                key: "approve",
                width: 90,
                align: "center",
                render: (_, record) => record.is_my_turn ? (
                    <Button
                        type="primary"
                        shape="circle"
                        icon={<CheckOutlined />}
                        style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                        onClick={() => setShowAcceptModal({ status: true, record })}
                    />
                ) : null
            },
            {
                title: "Reject",
                key: "reject",
                width: 90,
                align: "center",
                render: (_, record) => record.is_my_turn ? (
                    <Button
                        danger
                        type="primary"
                        shape="circle"
                        icon={<CloseOutlined />}
                        onClick={() => setShowRejectModal({ status: true, record })}
                    />
                ) : null
            }
        );
    }

    return (
        <>
            <ToastContainer position="top-right" autoClose={3000} />
            <center>
                <Title level={5} style={{ color: "#0e497b" }}>
                    PIP Approval Requests
                </Title>
            </center>

            {/* Status Filter Buttons with Counts */}
            <div style={{ textAlign: "center", marginBottom: "20px" }}>
                <Badge
                    count={countsLoading ? <LoadingOutlined style={{ color: "#faad14" }} /> : statusCounts.Pending}
                    showZero
                    color="#faad14"
                    offset={[-8, 4]}
                >
                    <Button
                        type={approvalStatusFilter === "Pending" ? "primary" : "default"}
                        onClick={() => setApprovalStatusFilter("Pending")}
                        style={{
                            marginRight: "18px",
                            backgroundColor: approvalStatusFilter === "Pending" ? "#faad14" : undefined,
                            borderColor: approvalStatusFilter === "Pending" ? "#faad14" : undefined,
                        }}
                    >
                        {approvalStatusFilter === "Pending" && loading
                            ? <><LoadingOutlined style={{ marginRight: 6 }} />Pending</>
                            : "Pending"}
                    </Button>
                </Badge>

                <Badge
                    count={countsLoading ? <LoadingOutlined style={{ color: "#4CAF50" }} /> : statusCounts.Approved}
                    showZero
                    color="#4CAF50"
                    offset={[-8, 4]}
                >
                    <Button
                        type={approvalStatusFilter === "Approved" ? "primary" : "default"}
                        onClick={() => setApprovalStatusFilter("Approved")}
                        style={{
                            marginRight: "18px",
                            backgroundColor: approvalStatusFilter === "Approved" ? "#4CAF50" : undefined,
                            borderColor: approvalStatusFilter === "Approved" ? "#4CAF50" : undefined,
                        }}
                    >
                        {approvalStatusFilter === "Approved" && loading
                            ? <><LoadingOutlined style={{ marginRight: 6 }} />Approved</>
                            : "Approved"}
                    </Button>
                </Badge>

                <Badge
                    count={countsLoading ? <LoadingOutlined style={{ color: "#f5222d" }} /> : statusCounts.Rejected}
                    showZero
                    color="#f5222d"
                    offset={[-8, 4]}
                >
                    <Button
                        type={approvalStatusFilter === "Rejected" ? "primary" : "default"}
                        onClick={() => setApprovalStatusFilter("Rejected")}
                        style={{
                            marginRight: "10px",
                            backgroundColor: approvalStatusFilter === "Rejected" ? "#f5222d" : undefined,
                            borderColor: approvalStatusFilter === "Rejected" ? "#f5222d" : undefined,
                        }}
                    >
                        {approvalStatusFilter === "Rejected" && loading
                            ? <><LoadingOutlined style={{ marginRight: 6 }} />Rejected</>
                            : "Rejected"}
                    </Button>
                </Badge>
            </div>
            {/* Filters Section */}
            <div className="filters-container">
                <Row gutter={[12, 12]} justify="center" align="middle">
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Employee Code"
                            value={empCodeFilter}
                            onChange={(e) => setEmpCodeFilter(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Input
                            placeholder="Employee Name"
                            value={empNameFilter}
                            onChange={(e) => setEmpNameFilter(e.target.value)}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            placeholder="Select Organisation"
                            style={{ width: "100%" }}
                            value={organisationFilter || undefined}
                            allowClear
                            onChange={handleOrganisationChange}
                        >
                            {organisations.map(org => <Option key={org.id} value={org.name}>{org.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            showSearch
                            placeholder="Select Department"
                            style={{ width: "100%" }}
                            value={departmentFilter || undefined}
                            allowClear
                            disabled={!organisationFilter}
                            onChange={handleDepartmentChange}
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {departments.map(dept => <Option key={dept.id} value={dept.name}>{dept.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={6} lg={3}>
                        <Select
                            placeholder="Select Designation"
                            style={{ width: "100%" }}
                            value={designationFilter || undefined}
                            allowClear
                            disabled={!departmentFilter}
                            onChange={(value) => setDesignationFilter(value)}
                            showSearch
                            filterOption={(input, option) => option?.children?.toLowerCase().includes(input.toLowerCase())}
                        >
                            {designations.map(des => <Option key={des.id} value={des.name}>{des.name}</Option>)}
                        </Select>
                    </Col>
                    <Col xs={24} sm={12} md={8} lg={5}>
                        <RangePicker
                            value={dateRangeFilter}
                            onChange={(dates) => setDateRangeFilter(dates || [])}
                            style={{ width: "100%" }}
                        />
                    </Col>
                    <Col xs={24} sm={12} md={4} lg={4}>
                        <Button
                            type="primary"
                            style={{
                                background: "#0e497b",
                                borderColor: "#0e497b",
                                width: "100%",
                            }}
                            onClick={downloadExcel}
                        >
                            Download Excel
                        </Button>
                    </Col>
                </Row>
            </div>

            {/* Table */}
            <div className="table-responsive">
                <Table
                    columns={columns}
                    dataSource={data}
                    rowKey="id"
                    pagination={{ pageSize: 25 }}
                    loading={{
                        spinning: loading,
                        indicator: <LoadingOutlined style={{ fontSize: 36, color: "#0e497b" }} spin />,
                        tip: "Loading PIP requests...",
                    }}
                    components={{
                        header: {
                            cell: (props) => (
                                <th {...props} style={{ backgroundColor: "#0e497b", color: "white" }}>
                                    {props.children}
                                </th>
                            ),
                        },
                    }}
                    bordered
                    scroll={{ x: "max-content" }}
                />
            </div>

            {/* Approve Modal */}
            <Modal
                title="Approval Remarks"
                open={showAcceptModal.status}
                footer={null}
                onCancel={() => { setShowAcceptModal({ status: false, record: null }); setRemarks(""); }}
                width={600}
                centered
            >
                <TextArea
                    rows={4}
                    placeholder="Enter remarks here (optional)..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button
                        style={{ marginRight: "8px" }}
                        onClick={() => { setShowAcceptModal({ status: false, record: null }); setRemarks(""); }}
                        disabled={processing}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        loading={processing}
                        disabled={processing}
                        style={{ backgroundColor: "#4CAF50", borderColor: "#4CAF50" }}
                        onClick={() => handleDecision(showAcceptModal.record, "Approved")}
                    >
                        {processing ? "Approving..." : "Approve"}
                    </Button>
                </div>
            </Modal>

            {/* Reject Modal */}
            <Modal
                title="Reason for Rejection"
                open={showRejectModal.status}
                footer={null}
                onCancel={() => { setShowRejectModal({ status: false, record: null }); setRemarks(""); }}
                width={600}
                centered
            >
                <TextArea
                    rows={4}
                    placeholder="Enter reason here..."
                    value={remarks}
                    onChange={(e) => setRemarks(e.target.value)}
                    style={{ borderRadius: "8px", marginBottom: "16px" }}
                />
                <div style={{ textAlign: "right" }}>
                    <Button
                        style={{ marginRight: "8px" }}
                        onClick={() => { setShowRejectModal({ status: false, record: null }); setRemarks(""); }}
                        disabled={processing}
                    >
                        Cancel
                    </Button>
                    <Button
                        type="primary"
                        danger
                        loading={processing}
                        disabled={processing || !remarks.trim()}
                        onClick={() => handleDecision(showRejectModal.record, "Rejected")}
                    >
                        {processing ? "Rejecting..." : "Reject"}
                    </Button>
                </div>
            </Modal>
        </>
    );
}